/**
 * Skeleton loading states — theme-aware gradients for light/dark surfaces.
 */

import React from "react";
import { Card, Col, Container, Row } from "react-bootstrap";

const skeletonStyles = `
  @keyframes lw-skeleton-shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .skeleton-line,
  .skeleton-circle,
  .skeleton-top-accent {
    background: linear-gradient(
      90deg,
      var(--color-surface-variant, #f5f5f7) 0%,
      var(--color-border-light, #e5e5e7) 50%,
      var(--color-surface-variant, #f5f5f7) 100%
    );
    background-size: 200% 100%;
    animation: lw-skeleton-shimmer 1.5s ease-in-out infinite;
  }

  .skeleton-line {
    border-radius: 6px;
  }

  .skeleton-circle {
    border-radius: 50%;
    flex-shrink: 0;
  }

  .skeleton-top-accent {
    border-radius: 0;
  }
`;

if (typeof document !== "undefined") {
  const STYLE_ID = "lw-skeleton-styles";
  if (!document.getElementById(STYLE_ID)) {
    const styleSheet = document.createElement("style");
    styleSheet.id = STYLE_ID;
    styleSheet.type = "text/css";
    styleSheet.innerText = skeletonStyles;
    document.head.appendChild(styleSheet);
  }
}

export const ContentCardSkeleton = () => {
  return (
    <Card className="border-0 h-100 content-card" style={{ backgroundColor: "var(--color-card-bg)" }}>
      <div className="skeleton-top-accent" style={{ height: "4px", width: "100%" }} />
      <Card.Body style={{ padding: "1.5rem" }}>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <div className="skeleton-line" style={{ height: "24px", width: "80%", marginBottom: "8px" }} />
            <div className="skeleton-line" style={{ height: "16px", width: "60%" }} />
          </div>
          <div className="skeleton-circle" style={{ width: "32px", height: "32px" }} />
        </div>

        <div className="mb-3">
          <div className="skeleton-line" style={{ height: "14px", width: "100%", marginBottom: "8px" }} />
          <div className="skeleton-line" style={{ height: "14px", width: "90%", marginBottom: "8px" }} />
          <div className="skeleton-line" style={{ height: "14px", width: "75%" }} />
        </div>

        <div className="d-flex justify-content-between align-items-center mt-auto">
          <div className="d-flex gap-3">
            <div className="skeleton-line" style={{ height: "20px", width: "60px" }} />
            <div className="skeleton-line" style={{ height: "20px", width: "60px" }} />
          </div>
          <div className="skeleton-line" style={{ height: "16px", width: "80px" }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export const ListSkeleton = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <ContentCardSkeleton key={index} />
    ))}
  </>
);

export const ContentGridSkeleton = ({ count = 6 }) => (
  <Row className="g-3">
    {Array.from({ length: count }).map((_, i) => (
      <Col key={i} xs={12} md={6} lg={4}>
        <ContentCardSkeleton />
      </Col>
    ))}
  </Row>
);

/** Full-viewport shell for lazy routes and auth gates */
export const AppShellSkeleton = ({ message = "" }) => (
  <div
    className="min-vh-100"
    style={{ background: "var(--color-bg)" }}
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div className="skeleton-line w-100 rounded-0" style={{ height: "52px", maxWidth: "100%" }} />
    <Container fluid className="py-4 px-3" style={{ maxWidth: 1280 }}>
      <div className="skeleton-line mb-4" style={{ height: "28px", width: "min(320px, 55%)" }} />
      <ContentGridSkeleton count={6} />
      {message ? <p className="text-center text-muted small mt-4 mb-0">{message}</p> : null}
    </Container>
  </div>
);

export const WriterDirectoryCardSkeleton = () => (
  <Card className="border-0 shadow-sm rounded-4 h-100">
    <Card.Body>
      <div className="d-flex gap-3 align-items-start">
        <div className="skeleton-circle" style={{ width: "56px", height: "56px" }} />
        <div className="flex-grow-1 min-w-0">
          <div className="skeleton-line mb-2" style={{ height: "18px", width: "65%" }} />
          <div className="skeleton-line mb-2" style={{ height: "14px", width: "45%" }} />
          <div className="skeleton-line" style={{ height: "14px", width: "88%" }} />
        </div>
      </div>
    </Card.Body>
  </Card>
);

export const WriterGridSkeleton = ({ count = 6 }) => (
  <Row className="g-3">
    {Array.from({ length: count }).map((_, i) => (
      <Col key={i} xs={12} sm={6} lg={4}>
        <WriterDirectoryCardSkeleton />
      </Col>
    ))}
  </Row>
);

export const BookmarkListSkeleton = ({ rows = 6 }) => (
  <div className="d-flex flex-column gap-2 py-1">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="rounded-4 border py-3 px-3"
        style={{ background: "var(--color-card-bg)", borderColor: "var(--color-border-light)" }}
      >
        <div className="d-flex justify-content-between align-items-center gap-2">
          <div className="skeleton-line flex-grow-1" style={{ height: "16px", maxWidth: "70%" }} />
          <div className="skeleton-line flex-shrink-0" style={{ height: "22px", width: "64px", borderRadius: 999 }} />
        </div>
      </div>
    ))}
  </div>
);

export const NotificationListSkeleton = ({ rows = 6 }) => (
  <div className="d-flex flex-column gap-0">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="d-flex gap-3 py-3 border-bottom"
        style={{ borderColor: "var(--color-border-light)" }}
      >
        <div className="skeleton-circle" style={{ width: "40px", height: "40px" }} />
        <div className="flex-grow-1 min-w-0">
          <div className="skeleton-line mb-2" style={{ height: "14px", width: "75%" }} />
          <div className="skeleton-line" style={{ height: "12px", width: "40%" }} />
        </div>
      </div>
    ))}
  </div>
);

export const FollowFeedListSkeleton = ({ rows = 5 }) => (
  <Card className="border-0 shadow-sm mb-4">
    <Card.Body className="d-flex flex-column gap-2 py-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="rounded-3 py-2 px-3"
          style={{ background: "var(--color-bg-secondary)" }}
        >
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="skeleton-line flex-grow-1" style={{ height: "14px", maxWidth: "75%" }} />
            <div className="skeleton-line flex-shrink-0" style={{ height: "20px", width: "52px", borderRadius: 6 }} />
          </div>
        </div>
      ))}
    </Card.Body>
  </Card>
);

export const ContinueReadingSkeleton = () => (
  <Card className="border-0 shadow-sm mb-4 continue-reading-skeleton">
    <Card.Body className="py-4">
      <div className="d-flex flex-column gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="d-flex align-items-center gap-3">
            <div className="skeleton-line rounded" style={{ width: "48px", height: "48px", flexShrink: 0 }} />
            <div className="flex-grow-1 min-w-0">
              <div className="skeleton-line mb-2" style={{ height: "12px", width: "88px" }} />
              <div className="skeleton-line" style={{ height: "6px", width: "100%" }} />
            </div>
          </div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

/** Compact stat placeholder (e.g. landing hero counts) */
export const StatValueSkeleton = () => (
  <div className="skeleton-line mx-auto mx-md-0" style={{ height: "clamp(1.1rem, 2.5vw, 1.4rem)", width: "3.5rem", maxWidth: "100%" }} />
);

export const KpiNumberSkeleton = () => (
  <div className="text-end" style={{ minWidth: "72px" }}>
    <div className="skeleton-line ms-auto" style={{ height: "28px", width: "72px", borderRadius: "8px" }} />
  </div>
);

export const ChartPanelSkeleton = ({ height = 220 }) => {
  const maxBar = Math.max(48, height - 36);
  const scales = [0.35, 0.55, 0.3, 0.72, 0.45, 0.62, 0.38, 0.8, 0.33, 0.5, 0.42, 0.68];
  return (
    <div
      className="d-flex align-items-end justify-content-between gap-1 px-1"
      style={{ height, minHeight: height }}
      aria-hidden
    >
      {scales.map((s, i) => (
        <div
          key={i}
          className="skeleton-line flex-grow-1"
          style={{
            height: `${Math.round(maxBar * s)}px`,
            borderRadius: "6px 6px 2px 2px",
            maxWidth: 32,
          }}
        />
      ))}
    </div>
  );
};

export const DataTableSkeleton = ({ rows = 8, columns = 5 }) => (
  <div className="table-responsive">
    <table className="table table-sm align-middle mb-0">
      <thead>
        <tr className="border-bottom" style={{ borderColor: "var(--color-border-light)" }}>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="py-2">
              <div className="skeleton-line" style={{ height: "12px", width: `${48 + (i % 3) * 12}px` }} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: columns }).map((_, c) => (
              <td key={c} className="py-3">
                <div
                  className="skeleton-line"
                  style={{
                    height: "14px",
                    width: c === 0 ? "min(220px, 90%)" : `${45 + ((r + c) % 4) * 10}%`,
                    maxWidth: c === 0 ? 220 : 140,
                  }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Label + control-shaped blocks (settings forms) */
export const FormFieldsSkeleton = ({ fields = 5 }) => (
  <Card className="border-0 shadow-sm">
    <Card.Body>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="mb-3">
          <div className="skeleton-line mb-2" style={{ height: "12px", width: `${28 + (i % 3) * 8}%`, maxWidth: 200 }} />
          <div className="skeleton-line" style={{ height: i === 1 ? "72px" : "40px", width: "100%", borderRadius: "6px" }} />
        </div>
      ))}
    </Card.Body>
  </Card>
);

export const FormStackSkeleton = ({ rows = 5 }) => (
  <Card className="border-0 shadow-sm">
    <Card.Body>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="mb-4 pb-3 border-bottom" style={{ borderColor: "var(--color-border-light)" }}>
          <div className="d-flex align-items-center gap-3">
            <div className="skeleton-line rounded-pill" style={{ width: "44px", height: "24px", flexShrink: 0 }} />
            <div className="skeleton-line flex-grow-1" style={{ height: "16px", width: "min(55%, 280px)" }} />
          </div>
          <div className="skeleton-line mt-2" style={{ height: "12px", width: "min(75%, 400px)", marginLeft: "56px" }} />
        </div>
      ))}
    </Card.Body>
  </Card>
);

/** Inline placeholder while a single content record is resolving */
export const ContentViewLoadingSkeleton = () => (
  <div className="py-3 px-2" style={{ maxWidth: 400, margin: "0 auto" }} role="status" aria-live="polite" aria-busy="true">
    <div className="skeleton-line mx-auto mb-3" style={{ height: "28px", width: "88%" }} />
    <div className="skeleton-line mx-auto mb-2" style={{ height: "12px", width: "55%" }} />
    <div className="skeleton-line mx-auto mb-2" style={{ height: "12px", width: "40%" }} />
    <div className="skeleton-line mx-auto" style={{ height: "12px", width: "70%" }} />
  </div>
);

export const ContentViewBodySkeleton = () => (
  <div className="content-view-loading py-4 px-2" role="status" aria-live="polite" aria-busy="true">
    <p className="small text-muted mb-3">Preparing this piece…</p>
    <div className="skeleton-line mb-2" style={{ height: "14px", width: "100%" }} />
    <div className="skeleton-line mb-2" style={{ height: "14px", width: "100%" }} />
    <div className="skeleton-line mb-2" style={{ height: "14px", width: "94%" }} />
    <div className="skeleton-line mb-2" style={{ height: "14px", width: "88%" }} />
    <div className="skeleton-line" style={{ height: "14px", width: "52%" }} />
  </div>
);

export const ProfilePageSkeleton = () => (
  <div className="profile-page min-vh-100 py-4" style={{ background: "var(--color-bg)" }}>
    <Container fluid style={{ maxWidth: 960 }}>
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
        <div className="skeleton-line w-100 rounded-0" style={{ height: "120px" }} />
        <Card.Body className="pt-0" style={{ marginTop: "-48px" }}>
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="skeleton-circle" style={{ width: "96px", height: "96px" }} />
            <div className="flex-grow-1 min-w-0 pb-2">
              <div className="skeleton-line mb-2" style={{ height: "26px", width: "min(280px, 55%)" }} />
              <div className="skeleton-line" style={{ height: "14px", width: "min(360px, 70%)" }} />
            </div>
          </div>
        </Card.Body>
      </Card>
      <Row className="g-3">
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="skeleton-line mb-3" style={{ height: "14px", width: i === 3 ? "45%" : "100%" }} />
              ))}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body>
              <div className="skeleton-line mb-3" style={{ height: "18px", width: "50%" }} />
              <div className="skeleton-line mb-2" style={{ height: "40px", width: "100%", borderRadius: 8 }} />
              <div className="skeleton-line" style={{ height: "40px", width: "100%", borderRadius: 8 }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </div>
);

export default ContentCardSkeleton;
